/**
 * Roles personalizados: permisos por sección (mejora 70, Alex, 8 sep 2026).
 *
 * Por qué existe. Los cinco roles fijos (`owner`, `staff`, `viewer`,
 * `kitchen`, `admin`) cubren los casos más comunes y nada más. Nos lo pidieron
 * varias veces con la misma forma: «un jefe de cocina que trabaje ingredientes,
 * recetas y costeo, pero que no vea ingresos ni gastos ni resultados». Cada
 * puesto de un restaurante real es un recorte distinto, y añadir un rol fijo
 * por puesto no acaba nunca.
 *
 * Así que el propietario compone el suyo: le pone nombre («Jefe de cocina»,
 * «Administración», «Compras»), elige qué puede ver y qué puede editar en cada
 * sección, y se lo asigna a quien quiera.
 *
 * Este fichero es la ÚNICA fuente de verdad de esa cuadrícula: lo usan la API
 * para cerrar las rutas y la web para esconder el menú. Si divergieran, la web
 * enseñaría puertas que la API cierra, que es exactamente la sensación de
 * software roto que hay que evitar.
 */

/** Secciones sobre las que se dan permisos. Son las pantallas de la app. */
export type PermissionModule =
  | 'panel'
  | 'asistente'
  | 'ingredientes'
  | 'inventario'
  | 'proveedores'
  | 'recetas'
  | 'precios'
  | 'ingresos'
  | 'gastos'
  | 'analisis'
  | 'resultados'
  | 'tesoreria'
  | 'inversiones'
  | 'objetivos'
  | 'impuestos'
  | 'ajustes';

export type PermissionLevel = 'ninguno' | 'ver' | 'editar';

/** Lo que no está en el mapa es `ninguno`: un rol nuevo nace sin permisos. */
export type RolePermissions = Partial<Record<PermissionModule, PermissionLevel>>;

export interface CustomRole {
  tenantId: string;
  roleId: string;
  name: string;
  permissions: RolePermissions;
  createdAt: string;
  updatedAt?: string;
  /** Rol de Alexia listo para usar; se puede asignar, pero no editar ni borrar. */
  preset?: boolean;
}

export interface CustomRoleInput {
  name: string;
  permissions: RolePermissions;
}

/** Orden y etiquetas del editor: el mismo orden que el menú de la app. */
export const PERMISSION_MODULES: PermissionModule[] = [
  'panel',
  'asistente',
  'ingredientes',
  'inventario',
  'proveedores',
  'recetas',
  'precios',
  'ingresos',
  'gastos',
  'analisis',
  'resultados',
  'tesoreria',
  'inversiones',
  'objetivos',
  'impuestos',
  'ajustes',
];

export const PERMISSION_MODULE_LABELS: Record<PermissionModule, string> = {
  panel: 'Panel general',
  asistente: 'Alexia, tu asistente',
  ingredientes: 'Ingredientes',
  inventario: 'Inventario',
  proveedores: 'Proveedores',
  recetas: 'Recetas y costeo',
  precios: 'Precios de la carta',
  ingresos: 'Ingresos',
  gastos: 'Gastos',
  analisis: 'Análisis',
  resultados: 'Cuenta de resultados',
  tesoreria: 'Tesorería',
  inversiones: 'Inversiones',
  objetivos: 'Objetivos',
  impuestos: 'Impuestos',
  ajustes: 'Ajustes',
};

/** Ruta de la web de cada sección, para saber a dónde llevar a cada rol. */
export const PERMISSION_MODULE_PATHS: Record<PermissionModule, string> = {
  panel: '/',
  asistente: '/asistente',
  ingredientes: '/ingredientes',
  inventario: '/inventario',
  proveedores: '/proveedores',
  recetas: '/recetas',
  precios: '/precios',
  ingresos: '/ingresos',
  gastos: '/gastos',
  analisis: '/analisis',
  resultados: '/resultados',
  tesoreria: '/tesoreria',
  inversiones: '/inversiones',
  objetivos: '/objetivos',
  impuestos: '/impuestos',
  ajustes: '/ajustes',
};

/**
 * Secciones cuyo dato lo escribe siempre el propietario (IVA, comisiones,
 * plataformas, locales, plan y equipo). Aquí «editar» no existe: el permiso
 * decide si la pantalla se ve, nada más. Se dice en el editor para que nadie
 * conceda algo que luego la API va a rechazar.
 */
export const MODULOS_SOLO_LECTURA: PermissionModule[] = ['ajustes'];

/**
 * Dependencias de LECTURA: para que esa pantalla funcione hace falta poder
 * leer estas otras. No son opciones, son cañerías — el editor de recetas
 * necesita la lista de ingredientes, y sin ella lo que se ve es una pantalla
 * a medias con un 403 detrás. Se conceden solas y se cuentan en el editor.
 */
export const DEPENDENCIAS_LECTURA: Partial<Record<PermissionModule, PermissionModule[]>> = {
  ingredientes: ['proveedores'],
  inventario: ['ingredientes'],
  recetas: ['ingredientes'],
  precios: ['recetas'],
  gastos: ['proveedores'],
  analisis: ['precios'],
};

/**
 * Dependencias de EDICIÓN: esa pantalla escribe de verdad en la otra sección,
 * así que el permiso tiene que llegar al mismo nivel.
 *
 * - Impuestos reparte un pago creando un gasto por mes.
 * - Análisis es donde se teclean las unidades vendidas de cada plato.
 */
export const DEPENDENCIAS_EDICION: Partial<Record<PermissionModule, PermissionModule[]>> = {
  impuestos: ['gastos'],
  analisis: ['precios'],
};

const ORDEN: Record<PermissionLevel, number> = { ninguno: 0, ver: 1, editar: 2 };

/** ¿`nivel` llega a `minimo`? */
export function alcanza(nivel: PermissionLevel, minimo: PermissionLevel): boolean {
  return ORDEN[nivel] >= ORDEN[minimo];
}

const mayor = (a: PermissionLevel, b: PermissionLevel): PermissionLevel =>
  ORDEN[a] >= ORDEN[b] ? a : b;

/**
 * Resuelve el mapa que el propietario ha marcado en el mapa que se aplica de
 * verdad, arrastrando las dependencias hasta que no cambia nada (`precios`
 * arrastra `recetas`, que arrastra `ingredientes`, que arrastra
 * `proveedores`).
 */
export function resolverPermisos(permissions: RolePermissions): RolePermissions {
  const efectivo: RolePermissions = { ...permissions };
  // Como mucho hacen falta tantas vueltas como módulos: la cadena más larga
  // no puede visitar dos veces el mismo.
  for (let vuelta = 0; vuelta < PERMISSION_MODULES.length; vuelta += 1) {
    let cambio = false;
    for (const modulo of PERMISSION_MODULES) {
      const nivel = efectivo[modulo] ?? 'ninguno';
      if (nivel === 'ninguno') continue;
      for (const dep of DEPENDENCIAS_LECTURA[modulo] ?? []) {
        const nuevo = mayor(efectivo[dep] ?? 'ninguno', 'ver');
        if (nuevo !== efectivo[dep]) {
          efectivo[dep] = nuevo;
          cambio = true;
        }
      }
      for (const dep of DEPENDENCIAS_EDICION[modulo] ?? []) {
        const nuevo = mayor(efectivo[dep] ?? 'ninguno', nivel);
        if (nuevo !== efectivo[dep]) {
          efectivo[dep] = nuevo;
          cambio = true;
        }
      }
    }
    if (!cambio) break;
  }
  return efectivo;
}

export function nivelDe(
  permissions: RolePermissions | undefined,
  modulo: PermissionModule,
): PermissionLevel {
  return permissions?.[modulo] ?? 'ninguno';
}

export const puedeVer = (p: RolePermissions | undefined, m: PermissionModule): boolean =>
  alcanza(nivelDe(p, m), 'ver');

export const puedeEditar = (p: RolePermissions | undefined, m: PermissionModule): boolean =>
  alcanza(nivelDe(p, m), 'editar');

/* ------------------------------------------------------------------ */
/* Rutas de la API                                                     */
/* ------------------------------------------------------------------ */

/**
 * Rutas que puede usar cualquier miembro, tenga el rol que tenga: quién soy,
 * el tour visto, los ajustes del restaurante (moneda e IVA, que hacen falta
 * para pintar cualquier cifra), los locales y las facturas de la suscripción.
 * Escribir en ellas ya exige propietario en la propia ruta.
 */
const PREFIJOS_LIBRES = ['/v1/me', '/v1/settings', '/v1/locations', '/v1/billing'];

/**
 * Qué sección gobierna cada ruta. LISTA BLANCA a propósito, igual que la
 * guarda de cocina: una ruta nueva nace cerrada para los roles personalizados
 * y hay que decir aquí a qué sección pertenece. Es más incómodo y es la razón
 * de que no se escape nada por descuido.
 *
 * Las entradas más específicas van primero: gana la primera que encaja.
 */
const RUTAS: Array<{ prefijo: string; modulos: PermissionModule[] }> = [
  { prefijo: '/v1/analytics/pnl', modulos: ['resultados'] },
  { prefijo: '/v1/analytics/kpis', modulos: ['analisis'] },
  { prefijo: '/v1/analytics/weekday', modulos: ['analisis'] },
  { prefijo: '/v1/analytics/dashboard', modulos: ['panel'] },
  { prefijo: '/v1/analytics/cashflow', modulos: ['tesoreria'] },
  { prefijo: '/v1/analytics/targets', modulos: ['objetivos'] },
  { prefijo: '/v1/analytics/tax-allocation', modulos: ['impuestos'] },
  { prefijo: '/v1/ingredients', modulos: ['ingredientes'] },
  // Alta masiva del catálogo: entran ingredientes y recetas en el mismo sitio.
  { prefijo: '/v1/import', modulos: ['ingredientes', 'recetas'] },
  { prefijo: '/v1/suppliers', modulos: ['proveedores'] },
  { prefijo: '/v1/recipes', modulos: ['recetas'] },
  // Las fotos son de las fichas de receta.
  { prefijo: '/v1/photos', modulos: ['recetas'] },
  { prefijo: '/v1/menu-items', modulos: ['precios'] },
  { prefijo: '/v1/menu', modulos: ['precios'] },
  { prefijo: '/v1/inventory', modulos: ['inventario'] },
  // El escáner sirve para albaranes de gasto y para hojas de recuento.
  { prefijo: '/v1/receipts', modulos: ['gastos', 'inventario'] },
  { prefijo: '/v1/revenues', modulos: ['ingresos'] },
  // El TPV conectado trae las ventas del día.
  { prefijo: '/v1/integrations', modulos: ['ingresos'] },
  { prefijo: '/v1/expenses', modulos: ['gastos'] },
  { prefijo: '/v1/investments', modulos: ['inversiones'] },
  { prefijo: '/v1/cash-movements', modulos: ['tesoreria'] },
  { prefijo: '/v1/targets', modulos: ['objetivos'] },
  { prefijo: '/v1/assistant', modulos: ['asistente'] },
];

const encaja = (path: string, prefijo: string): boolean =>
  path === prefijo || path.startsWith(`${prefijo}/`);

export type RutaPermiso =
  | { tipo: 'libre' }
  | { tipo: 'modulos'; modulos: PermissionModule[] }
  | { tipo: 'desconocida' };

export function permisoDeRuta(path: string): RutaPermiso {
  if (PREFIJOS_LIBRES.some((p) => encaja(path, p))) return { tipo: 'libre' };
  const regla = RUTAS.find((r) => encaja(path, r.prefijo));
  return regla ? { tipo: 'modulos', modulos: regla.modulos } : { tipo: 'desconocida' };
}

/**
 * ¿Puede este rol personalizado con esta petición? `permissions` tiene que
 * venir ya resuelto (`resolverPermisos`). Basta con tener el nivel en UNA de
 * las secciones de la ruta: el escáner vale tanto para quien lleva los gastos
 * como para quien hace el inventario.
 */
export function permiteRuta(
  permissions: RolePermissions,
  path: string,
  method: string,
): boolean {
  const regla = permisoDeRuta(path);
  if (regla.tipo === 'libre') return true;
  if (regla.tipo === 'desconocida') return false;
  const esLectura = ['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
  const minimo: PermissionLevel = esLectura ? 'ver' : 'editar';
  return regla.modulos.some((m) => alcanza(nivelDe(permissions, m), minimo));
}

/**
 * Primera pantalla que este rol puede abrir: a donde se le manda al entrar y
 * si teclea la URL de algo que no le toca. `null` si no puede ver nada, que
 * es un rol mal montado y hay que decirlo en vez de dejarle dando vueltas.
 */
export function portadaDePermisos(permissions: RolePermissions): string | null {
  const modulo = PERMISSION_MODULES.find((m) => puedeVer(permissions, m));
  return modulo ? PERMISSION_MODULE_PATHS[modulo] : null;
}

/**
 * Plantillas de partida del editor. No son roles fijos disfrazados: se cargan
 * en la cuadrícula y el propietario cambia lo que quiera antes de guardar.
 * Salen de los puestos que nos han pedido por su nombre.
 */
export interface PlantillaRol {
  name: string;
  descripcion: string;
  permissions: RolePermissions;
}

export const PLANTILLAS_ROL: PlantillaRol[] = [
  {
    name: 'Jefe de cocina',
    descripcion: 'Trabaja el costeo entero y no ve nada del dinero del negocio.',
    permissions: {
      ingredientes: 'editar',
      inventario: 'editar',
      proveedores: 'editar',
      recetas: 'editar',
      precios: 'editar',
    },
  },
  {
    name: 'Encargado de sala',
    descripcion: 'Registra las ventas del día y consulta la carta.',
    permissions: {
      panel: 'ver',
      ingresos: 'editar',
      precios: 'ver',
      recetas: 'ver',
    },
  },
  {
    name: 'Administración',
    descripcion: 'Lleva ingresos, gastos e impuestos y ve los resultados.',
    permissions: {
      panel: 'ver',
      ingresos: 'editar',
      gastos: 'editar',
      impuestos: 'editar',
      inversiones: 'editar',
      resultados: 'ver',
      analisis: 'ver',
      objetivos: 'ver',
    },
  },
  {
    name: 'Compras',
    descripcion: 'Mantiene proveedores, precios de compra e inventario.',
    permissions: {
      ingredientes: 'editar',
      proveedores: 'editar',
      inventario: 'editar',
      gastos: 'editar',
      recetas: 'ver',
    },
  },
];

/**
 * Perfiles de restaurante de la primera versión. Se implementan sobre la
 * cuadrícula ya existente para no migrar ni alterar a ningún usuario activo.
 */
export const PREDEFINED_ROLES: CustomRole[] = [
  {
    tenantId: '', roleId: 'preset:manager', name: 'Gerente', preset: true,
    createdAt: '2026-09-08T00:00:00.000Z',
    permissions: {
      panel: 'editar', asistente: 'editar', ingredientes: 'editar', inventario: 'editar',
      proveedores: 'editar', recetas: 'editar', precios: 'editar', ingresos: 'editar',
      gastos: 'editar', analisis: 'editar', resultados: 'ver', tesoreria: 'editar',
      inversiones: 'editar', objetivos: 'editar', impuestos: 'editar',
    },
  },
  {
    tenantId: '', roleId: 'preset:kitchen_manager', name: 'Jefe de cocina', preset: true,
    createdAt: '2026-09-08T00:00:00.000Z',
    permissions: {
      ingredientes: 'editar', inventario: 'editar', proveedores: 'editar',
      recetas: 'editar', precios: 'editar',
    },
  },
  {
    tenantId: '', roleId: 'preset:administration', name: 'Administración', preset: true,
    createdAt: '2026-09-08T00:00:00.000Z',
    permissions: {
      ingresos: 'editar', gastos: 'editar', proveedores: 'editar', analisis: 'ver',
      resultados: 'ver', impuestos: 'editar',
    },
  },
  {
    tenantId: '', roleId: 'preset:shift_manager', name: 'Encargado', preset: true,
    createdAt: '2026-09-08T00:00:00.000Z',
    permissions: {
      panel: 'ver', ingresos: 'editar', inventario: 'editar', proveedores: 'editar',
      precios: 'editar',
    },
  },
];

export function predefinedRole(roleId: string): CustomRole | null {
  return PREDEFINED_ROLES.find((role) => role.roleId === roleId) ?? null;
}
